import { CustomerAddressDetails, removeNullValues } from '@common';
import { FormOption } from '../../shared/models/form-option.interface';
import { AddressSearchResultOverview } from '../models/address-search-result/address-search-result-overview.interface';
import { AddressSearchResult } from '../models/address-search-result/address-search-result.interface';
import { FormAddress } from '../models/form-address/form-address.interface';
import { FormManualAddress } from '../models/form-manual-address/form-manual-address.interface';
import { FormOverrideAddress } from '../models/form-override-address/form-override-address.interface';

export function formatAddressProvinceNameFormOptions(
  provinceName: Map<string, string>,
): FormOption[] {
  return Array.from(provinceName.values()).reduce(
    (formOptions: FormOption<string>[], value: string) => [
      ...formOptions,
      { label: value, value },
    ],
    [],
  );
}

export function formatFormAddressToCustomerAddress(
  address: FormOverrideAddress | FormAddress | FormManualAddress,
): CustomerAddressDetails {
  address = removeNullValues(address);

  return {
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2 ?? '',
    addressLine3: address.addressLine3,
    addressLine4: address.addressLine4,
    countryCode: address.countryCode,
    countryIso2: address.countryIso2,
    countryIso3: address.countryIso3,
    countryIsoNumber: address.countryIsoNumber,
    countryName: address.countryName,
    id: 'id' in address ? address.id : undefined,
    postalCode: address.postalCode,
    type: 'type' in address ? address.type : undefined,
  };
}

export function formatAddressSearchResultToFormAddress(
  address: AddressSearchResult,
): FormAddress {
  const {
    BuildingName,
    BuildingNumber,
    City,
    ProvinceName,
    Street,
    SubBuilding,
  } = address;

  const addressLines: string[] = [
    address.Company,
    address.Line1,
    address.Line2,
    address.Line3,
  ].filter(Boolean) as string[];

  let addressLine1: string = addressLines[0];
  let addressLine2: string = addressLines[1] || '';
  let addressLine3: string = addressLines[2] || City;
  const addressLine4: string = ProvinceName || City;

  // Flats and Apartments
  if (SubBuilding && BuildingName) {
    addressLine1 = `${SubBuilding} ${BuildingName}`;
    addressLine2 = [BuildingNumber, Street].filter(Boolean).join(' ');
    addressLine3 = City;
  }

  // Provincial cities
  if (addressLine3 === addressLine4) {
    addressLine3 = '';
  }

  return {
    id: address.Id,
    addressLine1,
    ...(addressLine2 ? { addressLine2: addressLine2 } : {}),
    addressLine3,
    addressLine4,
    postalCode: address.PostalCode,
    countryCode: address.CountryIso3,
    countryIso2: address.CountryIso2,
    countryIso3: address.CountryIso3,
    countryIsoNumber: address.CountryIsoNumber,
    countryName: address.CountryName,
    type: address.Type,
  };
}

export function formatAddressSearchResultToOverview(
  address: AddressSearchResult,
): AddressSearchResultOverview {
  const { City, Company, Id, Line1, Line2, PostalCode, Type } = address;

  const Description: string = [City, PostalCode].filter(Boolean).join(', ');
  const Text: string = [Company, Line1, Line2].filter(Boolean).join(', ');

  return {
    Id,
    Description,
    Highlight: '',
    Text,
    Type,
  };
}
